// How long an execution actually runs.
//
// Three shapes say "length" and only one of them says `plannedLength`:
//
//   coiling                            coilCount   × coilLength
//   rewinding                          rewindCount × rewindLength
//   exterior_sheat + coils_on_machine  coilCount   × coilLength
//
// The third is the extruder winding its own coils rather than sending the cable
// on to a coiler, and it was the shape nobody derived. The plan-next-phase
// wizard filled `plannedLength` in for the first two and left this one at 0, so
// the server rejected a perfectly valid plan with "Execution plannedLength must
// be > 0 (groupKey=…::exterior_sheat)" — a run of ten 100 m coils plus a 9,000 m
// drum, adding up to exactly the 10,000 m available.
//
// `to_coiling` and `to_rewinding` are deliberately NOT derived: those send the
// cable onward to another machine, so the length of THIS run is the cable's, not
// the coils some later machine will wind out of it.
//
// The same fields live at the top level in a create-new payload and under
// `executionMeta` in a plan-next-phase one, so both are read. Pure.
//
// COPIE VERBATIM a redis_dev/helpers/execLength.js. CRA nu poate importa din
// afara src/, iar `tandemCheck.js` are nevoie de regula asta — fara ea a numarat
// o executie de 30 x 100 m colaci pe masina drept ZERO si a refuzat un tandem
// perfect valid (raportat 2026-10-27). execLength.test.js compara TEXTUL SURSA
// al celor doua copii, deci nu pot sa se desincronizeze.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Is this one of the shapes whose length is a count times a unit length?
 *
 * Returns the length, or `null` when the execution is not one of them — the
 * caller decides what to do with "not applicable", which is not the same answer
 * as "zero".
 */
export function coilShapeLength(exec, phaseName) {
  const meta =
    exec?.executionMeta && typeof exec.executionMeta === "object"
      ? exec.executionMeta
      : {};
  const pick = (k) => num(exec?.[k]) || num(meta?.[k]);
  const mode = String(exec?.deliveryMode || meta?.deliveryMode || "")
    .trim()
    .toLowerCase();
  const phase = String(phaseName || "").trim();

  if (phase === "coiling") return pick("coilCount") * pick("coilLength");
  if (phase === "rewinding") return pick("rewindCount") * pick("rewindLength");
  if (phase === "exterior_sheat" && mode === "coils_on_machine") {
    return pick("coilCount") * pick("coilLength");
  }
  return null;
}

/**
 * The length to plan, validate and build the task with — one answer, so the
 * three cannot disagree.
 *
 * An explicit `plannedLength` wins: a client that has already worked the number
 * out is not second-guessed. Otherwise the shape is derived. Returns 0 when
 * there is no length to be had, which is what the callers check for.
 */
export function execPlannedLength(exec, phaseName) {
  const direct = num(exec?.plannedLength);
  if (direct > 0) return direct;
  const shaped = coilShapeLength(exec, phaseName);
  return shaped != null && shaped > 0 ? shaped : 0;
}

export default execPlannedLength;
